import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'

// EP-150 (App Review 5.1.1v): account/login deletion, docs/ep-150-app-store-review.md §3.

export type AccountDeletionScope = 'account' | 'user'

export interface AccountDeletionStatus {
  scope: AccountDeletionScope
  status: 'none' | 'pending'
  requested_at: string | null
  scheduled_at: string | null
  grace_days: number
}

export const accountDeletionKeys = {
  all: ['account_deletion'] as const,
}

/**
 * GET /api/account_deletion. `status` is always "none" for anyone who can
 * call this: there is no in-app view of a pending request, since the
 * account/login stops working the moment one is filed.
 */
export function useAccountDeletionStatus() {
  return useQuery<AccountDeletionStatus>({
    queryKey: accountDeletionKeys.all,
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<AccountDeletionStatus>>('/api/account_deletion')
      return data.data
    },
  })
}

export interface RequestAccountDeletionInput {
  password: string
}

export interface RequestAccountDeletionResult {
  scope: AccountDeletionScope
  status: 'pending'
  requested_at: string
  scheduled_at: string
}

/**
 * POST /api/account_deletion. 201 on success; 422 `password_invalid` on a
 * wrong password. The tokens this request carried are revoked server-side
 * the moment it succeeds, so the caller clears them locally too (see
 * AccountDeletePage / AuthContext#clearSession).
 */
export function useRequestAccountDeletion() {
  return useMutation<RequestAccountDeletionResult, unknown, RequestAccountDeletionInput>({
    // The page shows the failure beside the field, in the person's language.
    // The backstop toast would repeat it in the server's English.
    meta: { suppressErrorToast: true },
    mutationFn: async (input) => {
      const { data } = await api.post<ApiResponse<RequestAccountDeletionResult>>(
        '/api/account_deletion',
        input,
      )
      return data.data
    },
  })
}
